/**
 * KBDrawingsPanel.jsx — Drawings tab embedded inside PortalKnowledge.
 *
 * A single KB can have text docs AND engineering drawings; this
 * component is only concerned with the drawings side. It handles:
 *  • drag / click upload with per-file progress
 *  • gallery grid keyed by thumbnail
 *  • click for detail modal (extracted schema, SVG preview, delete)
 */
import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Upload, Loader2, FileImage, Trash2, X, Sparkles, Info } from 'lucide-react';
import { drawingsApi } from '../../services/drawings';
import { useUploadStore, registerAbort } from '../../stores/uploadStore';
import { NexusCard, NexusButton, NexusBadge } from '../../components/nexus-ui';
import { toast } from '../../stores';

const ACCEPT = 'image/png,image/jpeg,image/webp,application/pdf';

export const KBDrawingsPanel = ({ kbId, readOnly = false, api = drawingsApi, queryKey = 'kb-drawings' }) => {
  const queryClient = useQueryClient();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [detail, setDetail] = useState(null);
  const [dragging, setDragging] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: [queryKey, kbId],
    queryFn: () => api.list(kbId).then((r) => r.data.items || []),
    enabled: !!kbId,
  });

  const uploadOne = useCallback(async (file) => {
    // Route through the global upload store so progress survives tab
    // navigation. The panel-local `uploading` flag stays for the drop
    // zone microcopy, but the actual promise is fire-and-forget.
    const uploadId = `${kbId}-${file.name}-${Date.now()}`;
    const store = useUploadStore.getState();
    const controller = new AbortController();
    registerAbort(uploadId, controller);
    store.start({ id: uploadId, filename: file.name, kbId, kbName: 'this KB', cancellable: true });
    setUploading(true);
    setProgress(0);
    // Do the actual upload in the background — this is critical so
    // switching tabs doesn't cancel the request.
    (async () => {
      try {
        await api.upload(kbId, file, (pct) => {
          store.setProgress(uploadId, pct);
          setProgress(pct);
          if (pct >= 100) store.setPhase(uploadId, 'extracting');
        }, { signal: controller.signal });
        store.finish(uploadId, {});
        toast.success(`Analysed ${file.name}`);
        queryClient.invalidateQueries({ queryKey: [queryKey, kbId] });
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
          // Already handled by uploadStore.cancel — don't overwrite with a generic error.
          return;
        }
        const msg = e?.response?.data?.detail || `Failed to upload ${file.name}`;
        store.finish(uploadId, { error: msg });
        toast.error(msg);
      } finally {
        setUploading(false);
        setProgress(0);
      }
    })();
  }, [kbId, api, queryClient, queryKey]);

  const handleFiles = useCallback(async (files) => {
    if (readOnly) {
      toast.error('Only admins can upload drawings.');
      return;
    }
    // Fire all uploads concurrently — they run through the background
    // store so tab-switching is safe.
    Array.from(files || []).forEach((f) => uploadOne(f));
  }, [uploadOne, readOnly]);

  const removeMutation = useMutation({
    mutationFn: (id) => api.remove(id),
    onSuccess: () => {
      toast.success('Drawing removed');
      setDetail(null);
      queryClient.invalidateQueries({ queryKey: [queryKey, kbId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Delete failed'),
  });

  const drawings = data || [];

  return (
    <div className="space-y-4" data-testid="kb-drawings-panel">
      {/* Upload zone */}
      {!readOnly && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => !uploading && inputRef.current?.click()}
          className="rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all"
          style={{
            border: dragging ? '2px dashed #F26B1F' : '2px dashed var(--hairline)',
            background: dragging ? 'rgba(242,107,31,0.05)' : 'var(--layer-1)',
          }}
          data-testid="kb-drawings-dropzone">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="kb-drawings-fileinput"
          />
          {uploading ? (
            <>
              <Loader2 size={22} className="animate-spin text-nexus-accent-orange" />
              <p className="mt-2 text-[12.5px] text-nexus-text-secondary">
                Analysing drawing… {progress > 0 && `(uploaded ${progress}%)`}
              </p>
              <p className="text-[10.5px] text-nexus-text-tertiary">
                Cognara Vision is extracting rooms, dimensions and SVG polylines. This takes 15–30 s per drawing.
              </p>
            </>
          ) : (
            <>
              <Upload size={22} className="text-nexus-text-tertiary" />
              <p className="mt-2 font-heading text-[14px] font-semibold text-nexus-text-primary">
                Drop a floor plan, elevation, or PDF here
              </p>
              <p className="text-[11.5px] text-nexus-text-tertiary mt-1">
                PNG / JPG / WEBP / PDF · up to 20 MB · Auto-extracts schema + description into the KB
              </p>
            </>
          )}
        </div>
      )}

      {/* Gallery grid */}
      {isLoading ? (
        <div className="p-8 text-nexus-text-tertiary text-sm">Loading drawings…</div>
      ) : drawings.length === 0 ? (
        <div className="p-8 text-center text-nexus-text-tertiary text-sm">
          <FileImage size={28} className="mx-auto opacity-50 mb-2" />
          No drawings yet. Upload one to start training the RAG on engineering assets.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {drawings.map((d) => (
            <motion.button
              key={d.id}
              layout
              whileHover={{ scale: 1.02 }}
              onClick={() => setDetail(d)}
              className="rounded-lg overflow-hidden text-left transition-all"
              style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
              data-testid={`kb-drawing-tile-${d.id}`}>
              <div className="aspect-[4/3] bg-nexus-bg-tertiary flex items-center justify-center overflow-hidden">
                <img
                  src={api.thumbUrl(d.id)}
                  alt={d.filename}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              <div className="p-2.5">
                <div className="text-[12px] font-medium text-nexus-text-primary truncate">{d.filename}</div>
                <div className="flex items-center justify-between mt-1">
                  <NexusBadge variant="default" size="xs">
                    {(d.drawing_type || 'other').replace('_', ' ')}
                  </NexusBadge>
                  <span className="text-[10px] text-nexus-text-tertiary">
                    {new Date(d.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <DrawingDetailModal
          drawingId={detail.id}
          filename={detail.filename}
          drawingType={detail.drawing_type}
          api={api}
          onClose={() => setDetail(null)}
          onDelete={() => removeMutation.mutate(detail.id)}
          canDelete={!readOnly}
        />
      )}
    </div>
  );
};

const DrawingDetailModal = ({ drawingId, filename, drawingType, api, onClose, onDelete, canDelete }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['kb-drawing', drawingId],
    queryFn: () => api.get(drawingId).then((r) => r.data),
  });
  // Multi-page PDF navigation — extraction.pages[] is present when the
  // upload was a multi-page PDF. Otherwise we render a single view.
  const pages = data?.extraction?.pages || null;
  const [pageIdx, setPageIdx] = useState(0);
  const currentPage = pages && pages.length > pageIdx ? pages[pageIdx] : null;
  const currentSvg = currentPage?.svg_hint || data?.extraction?.svg_hint;
  const currentDesc = currentPage?.description || data?.description;
  const houghSvg = data?.extraction?.hough_svg;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.65)' }}
      onClick={onClose}
      data-testid="kb-drawing-detail-modal">
      <div className="max-w-4xl w-full max-h-[92vh] overflow-y-auto rounded-xl"
        style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: 'var(--hairline)' }}>
          <div>
            <h2 className="font-heading text-lg font-semibold text-nexus-text-primary">{filename}</h2>
            <div className="flex items-center gap-2 mt-1">
              <NexusBadge variant="accent" size="xs">{(drawingType || 'other').replace('_', ' ')}</NexusBadge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canDelete && (
              <NexusButton variant="ghost" size="sm" onClick={onDelete} data-testid="kb-drawing-delete">
                <Trash2 size={14} /> Delete
              </NexusButton>
            )}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-nexus-bg-tertiary" data-testid="kb-drawing-close">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {isLoading ? (
            <div className="text-sm text-nexus-text-tertiary">Loading…</div>
          ) : !data ? (
            <div className="text-sm text-nexus-text-tertiary">Not found.</div>
          ) : (
            <>
              {/* Multi-page PDF pager — only shown when extraction has pages[] */}
              {pages && pages.length > 1 && (
                <div className="flex items-center gap-2 rounded-lg p-2"
                  style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
                  data-testid="kb-drawing-pager">
                  <span className="text-[11px] font-semibold text-nexus-text-tertiary uppercase">Page</span>
                  {pages.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setPageIdx(i)}
                      className="px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all"
                      style={{
                        background: pageIdx === i ? '#F26B1F' : 'var(--layer-0)',
                        color: pageIdx === i ? '#fff' : 'var(--text-primary)',
                        border: '1px solid var(--hairline)',
                      }}
                      data-testid={`kb-drawing-page-${i + 1}`}>
                      {i + 1}
                    </button>
                  ))}
                  <span className="text-[10px] text-nexus-text-tertiary ml-auto">
                    {pages.length} pages · {currentPage?.drawing_type || 'unknown'}
                  </span>
                </div>
              )}

              {/* Thumbnail */}
              <div className="rounded-lg overflow-hidden" style={{ background: 'var(--layer-1)' }}>
                <img src={api.thumbUrl(drawingId)} alt="" className="w-full max-h-[400px] object-contain" />
              </div>

              {/* Description */}
              {currentDesc && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 text-nexus-text-tertiary">
                    Description (indexed for RAG)
                  </div>
                  <p className="text-[13px] text-nexus-text-primary leading-relaxed">{currentDesc}</p>
                </div>
              )}

              {/* Extraction schema */}
              {data.extraction && (
                <details className="rounded-lg p-3" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
                  <summary className="text-[12px] font-semibold cursor-pointer text-nexus-text-primary">
                    Extracted schema (rooms, openings, annotations…)
                  </summary>
                  <pre className="mt-3 text-[10.5px] font-mono overflow-auto max-h-96 text-nexus-text-secondary whitespace-pre-wrap">
                    {JSON.stringify(currentPage || data.extraction, null, 2)}
                  </pre>
                </details>
              )}

              {/* SVG hint if present */}
              {currentSvg && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 text-nexus-text-tertiary flex items-center gap-1">
                    <Sparkles size={11} /> SVG polylines (rebuild-ready)
                  </div>
                  <div className="rounded-lg p-3 flex items-center justify-center"
                    style={{ background: '#FFFFFF', border: '1px solid var(--hairline)' }}
                    dangerouslySetInnerHTML={{ __html: currentSvg }} />
                </div>
              )}

              {/* OpenCV Hough overlay — pixel-space wall reinforcement */}
              {houghSvg && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 text-nexus-text-tertiary flex items-center gap-1">
                    <Sparkles size={11} /> Hough-line polylines (OpenCV — pixel-perfect walls)
                  </div>
                  <div className="rounded-lg p-3 flex items-center justify-center"
                    style={{ background: '#FFFFFF', border: '1px solid var(--hairline)' }}
                    dangerouslySetInnerHTML={{ __html: houghSvg }} />
                </div>
              )}

              <div className="rounded-lg p-3 flex items-start gap-2 text-[11.5px] text-nexus-text-tertiary"
                style={{ background: 'rgba(242,107,31,0.06)', border: '1px solid rgba(242,107,31,0.3)' }}>
                <Info size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#F26B1F' }} />
                <span>
                  The description above is embedded in your KB vector index — you can now retrieve this drawing
                  via natural language in Agent Chat, and use the extraction schema as reference material in the Design Studio.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
